'use client';

import React, { Suspense, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { useHasMounted } from '@/doi-tac-module/hooks/use-has-mounted';
import { roadmapTranslations as t } from '@/doi-tac-module/lib/i18n/bangladesh/quy-trinh-translations';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { consultants } from '@/doi-tac-module/lib/consultant-data';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { getAgencyProfile } from '@/doi-tac-module/actions/agency-actions';
interface AgencyInfo {
    name: string;
    company: string;
    avatarUrl: string;
    companyLogo?: string;
    role?: string;
}

// Transparent pixel for clean SVG generation
const TRANSPARENT_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

function ReferralContent() {
    const searchParams = useSearchParams();
    const { language } = useLanguage();
    const { user, isLoggedIn, updateCandidateAgency } = useAuth();
    const { toast } = useToast();
    const [refId, setRefId] = useState<string | null>(null);
    const hasMounted = useHasMounted();
    const [showConfirm, setShowConfirm] = useState(false);
    const [hasAgency, setHasAgency] = useState(false);

    const [agencyInfo, setAgencyInfo] = useState<AgencyInfo | null>(null);

    const getAgencyNameByCode = (code: string) => {
        // Mock mapping for demo: hash the code or just return a specific one
        // If code contains common sequences from test data, map them
        if (code.includes('MNAY')) return "M Nayon Overseas Limited";
        if (code.includes('GULAM')) return "Gulam Rabbi International Ltd.";
        if (code.includes('FLAM')) return "Flamenco Overseas Ltd.";
        if (code.includes('COUN')) return "M/S. Country Road International";

        // Default mapping based on the random part of the code for variation
        const index = code.length % consultants.filter(c => c.id !== 'mir-mohammad-naim').length;
        const validCompanies = consultants.filter(c => c.id !== 'mir-mohammad-naim').map(c => c.company);
        return validCompanies[index] || "M Nayon Overseas Limited";
    };

    useEffect(() => {
        const urlRef = searchParams.get('ref');

        // Strictly set refId from URL to control visibility as requested
        setRefId(urlRef);

        if (urlRef) {
            if (typeof window !== 'undefined') {
                sessionStorage.setItem('referral_code', urlRef);
            }
        }
    }, [searchParams]);
    useEffect(() => {
        if (!refId) return;
        const fetchAgency = async () => {
            try {
                const result = await getAgencyProfile(refId);
                if (result.success && result.data) {
                    const getSafeUrl = (file: any) => {
                        if (!file) return null;
                        if (typeof file === 'string') return file;
                        return file.url || null;
                    };
                    const avatarUrl =
                        getSafeUrl(result.data.representativePhoto) ||
                        getSafeUrl(result.data.companyLogo) ||
                        getSafeUrl(result.data.officePhoto) ||
                        '/img/BANGLADESH/AVATAR/BLD000.jpg';
                    setAgencyInfo({
                        name: result.data.managingDirector || result.data.companyName || 'HelloJob Agent',
                        company: result.data.companyName || 'HelloJob Bangladesh',
                        avatarUrl,
                        companyLogo: result.data.companyLogo,
                        role: result.data.role,
                    });
                }
            } catch {
                // silently fall back — card still renders without agency info
            }
        };
        fetchAgency();
    }, [refId]);

    useEffect(() => {
        if (hasMounted && isLoggedIn && user && user.role === 'company') {
            const checkAgency = () => {
                const profileRaw = localStorage.getItem('generatedCandidateProfile');
                if (profileRaw) {
                    try {
                        const profile = JSON.parse(profileRaw);
                        if (profile.bangladeshAgency) {
                            setHasAgency(true);
                            return;
                        }
                    } catch (e) { }
                }

                // Show confirmation if we have a refId and no agency yet
                if (refId) {
                    setShowConfirm(true);
                }
            };
            checkAgency();
        }
    }, [hasMounted, isLoggedIn, user, refId]);

    if (!hasMounted || !refId || hasAgency) return null;

    const shareUrl = hasMounted
        ? `${window.location.origin}/quy-trinh?ref=${refId}`
        : `https://hellojob.jp/quy-trinh?ref=${refId}`;
    const translations = t.referralCard as Record<string, { vi: string, bn: string }>;
    const agencyName = getAgencyNameByCode(refId);

    const handleConfirm = async () => {
        const success = await updateCandidateAgency(agencyName);
        if (success) {
            setHasAgency(true);
            setShowConfirm(false);
            toast({
                title: t.referralConfirmation.toastSuccess[language],
                className: "bg-green-600 text-white border-none"
            });
        }
    };
    return (
        <>
            <div className="w-full mt-8 relative z-20 max-w-3xl mx-auto px-4 md:px-0">
                {/* Card */}
                <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-slate-100 transition-all duration-500 hover:shadow-[0_30px_70px_-12px_rgba(0,0,0,0.2)] group/card">

                    {/* ── Header strip ── */}
                    <div className="bg-[#F2B92A] px-6 md:px-8 py-2.5 md:py-3.5 flex items-center shadow-sm relative z-10 min-h-[50px]">
                        <div className="text-white font-bold text-[14px] md:text-[15px] leading-snug">
                            {(() => {
                                const fullTitle = (agencyInfo?.role === 'MASTER_CTV' || agencyInfo?.role === 'ADMIN')
                                    ? translations.titleMaster?.[language]?.replace('🤝 ', '') || "Giới thiệu bởi Tổng đại lý - Đối tác chiến lược của HelloJob"
                                    : translations.titleAgent?.[language]?.replace('🤝 ', '') || "Giới thiệu bởi Đại lý cấp 1 - Đối tác phát triển của HelloJob";
                                const parts = fullTitle.split(' - ');
                                return (
                                    <p>
                                        <span className="inline-block mr-1">🤝</span>
                                        {parts[0]}
                                        {parts[1] && (
                                            <>
                                                <span className="hidden md:inline"> - </span>
                                                <br className="md:hidden" />
                                                <span className="md:inline-block">{parts[1]}</span>
                                            </>
                                        )}
                                    </p>
                                );
                            })()}
                        </div>
                    </div>

                    {/* ── Body ── */}
                    <div className="flex flex-col md:flex-row items-stretch">

                        {/* LEFT: QR — blue panel */}
                        <div className="bg-[#19A6DF] p-6 flex flex-col items-center justify-center shrink-0 gap-3 md:w-64 relative overflow-hidden">
                            {/* Subtle pattern for blue panel */}
                            <div className="absolute inset-0 opacity-10 pointer-events-none bg-[radial-gradient(circle_at_center,_white_1px,_transparent_1px)] bg-[length:15px_15px]" />

                            <div className="bg-white p-2.5 md:p-3 rounded-2xl relative shadow-2xl shadow-black/30 transition-transform duration-500 group-hover/card:scale-105 z-10">
                                <QRCodeSVG
                                    value={shareUrl}
                                    size={130}
                                    level={"H"}
                                    includeMargin={false}
                                    className="w-28 h-28 md:w-32 md:h-32 transition-transform duration-500 group-hover/card:rotate-1"
                                    imageSettings={{
                                        src: TRANSPARENT_PIXEL,
                                        height: 36,
                                        width: 36,
                                        excavate: true,
                                    }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="bg-white flex items-center justify-center w-[32px] h-[32px] md:w-[38px] md:h-[38px] rounded-xl shadow-sm p-1">
                                        <img src="/img/HJPNG.png" alt="logo" className="w-[24px] md:w-[28px] h-auto object-contain block" />
                                    </div>
                                </div>
                            </div>
                            <p className="text-white font-bold text-[11px] md:text-[12px] text-center leading-snug tracking-[0.2em] z-10 opacity-90">
                                {language === 'vi' ? 'Quét bằng Camera' : 'ক্যামেরা দিয়ে স্ক্যান'}
                            </p>
                        </div>

                        {/* RIGHT: Info */}
                        <div className="flex-1 flex flex-col justify-between p-6 md:p-7 gap-4 md:gap-6 min-w-0 bg-gradient-to-br from-white to-slate-50">

                            {/* Code Group */}
                            <div className="flex flex-col items-start gap-1">
                                <span className="text-[11px] md:text-[12px] font-bold text-[#19A6DF] tracking-[0.2em] opacity-80">
                                    {(agencyInfo?.role === 'MASTER_CTV' || agencyInfo?.role === 'ADMIN')
                                        ? (translations.codeLabelMaster?.[language] || "Mã Tổng đại lý:")
                                        : (translations.codeLabelAgent?.[language] || "Mã đại lý:")}
                                </span>
                                <div className="bg-slate-100/60 px-3 md:px-4 py-2 rounded-xl border border-slate-200/60 w-full max-w-[280px] sm:max-w-none overflow-hidden">
                                    <span className="text-[#19A6DF] font-bold text-[16px] sm:text-[20px] md:text-[24px] tracking-[0.2em] md:tracking-[0.4em] leading-none block text-center sm:text-left">
                                        {refId}
                                    </span>
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="h-px w-full bg-gradient-to-r from-slate-200 via-slate-100 to-transparent opacity-60" />

                            <div className="flex flex-col items-start gap-3 w-full">
                                {/* Row 1: Avatar + Name */}
                                <div className="flex items-center gap-3">
                                    <div className="relative shrink-0">
                                        <img
                                            src={agencyInfo?.avatarUrl ?? '/img/BANGLADESH/AVATAR/BLD000.jpg'}
                                            alt={agencyInfo?.name ?? ''}
                                            className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white shadow-md object-cover transition-all duration-500 group-hover/agent:border-[#F2B92A]/50"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = '/img/BANGLADESH/AVATAR/BLD000.jpg';
                                            }}
                                        />
                                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 md:w-3 md:h-3 bg-green-500 border-2 border-white rounded-full shadow-sm" />
                                    </div>
                                    <p className="text-slate-900 font-bold text-[18px] md:text-[20px] leading-tight tracking-tight">
                                        {agencyInfo?.name ?? 'HelloJob Sales Manager'}
                                    </p>
                                </div>

                                {/* Row 2: Company + Company Logo */}
                                {(agencyInfo?.company && agencyInfo.company !== agencyInfo.name) ? (
                                    <div className="flex items-start gap-1.5 min-w-0 w-full">
                                        {agencyInfo.companyLogo && (
                                            <img
                                                src={agencyInfo.companyLogo}
                                                alt="company logo"
                                                className="shrink-0 h-3.5 md:h-4 w-auto opacity-90 object-contain mt-1"
                                            />
                                        )}
                                        <span className="text-slate-500 text-[13px] md:text-[14px] font-medium leading-snug text-left break-words">
                                            {agencyInfo.company}
                                        </span>
                                    </div>
                                ) : null}

                                {/* Row 3: Badge */}
                                <div className="flex items-center">
                                    <span className="text-[#19A6DF] text-[10px] md:text-[11px] font-bold tracking-widest px-2 py-0.5 bg-blue-50/80 rounded-md border border-blue-100/50">
                                        {(agencyInfo?.role === 'MASTER_CTV' || agencyInfo?.role === 'ADMIN')
                                            ? (translations.partnerStrategic?.[language] || "Đối tác chiến lược của HelloJob")
                                            : (translations.partnerDevelopment?.[language] || "Đối tác phát triển của HelloJob")}
                                    </span>
                                </div>
                            </div>

                            {/* CTA Row */}
                            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 pt-1">
                                <p className="text-slate-500 text-[10px] md:text-[12px] font-medium leading-relaxed lg:max-w-[280px] text-center md:text-left opacity-70 italic">
                                    {translations.qrInstruction[language]}
                                </p>
                                <Button
                                    className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-medium h-12 px-8 md:px-10 rounded-2xl shadow-[0_10px_20px_-8px_rgba(242,185,42,0.4)] text-[15px] md:text-[17px] shrink-0 transition-all hover:scale-105 active:scale-95 w-full md:w-auto"
                                    asChild
                                >
                                    <Link 
                                        href={`/auth?ref=${refId}&redirect=${encodeURIComponent('/quy-trinh')}&scroll=${typeof window !== 'undefined' ? window.scrollY : 0}`}
                                        onClick={() => {
                                            if (typeof window !== 'undefined') {
                                                const scroll = window.scrollY;
                                                setNavigatingPath(`/auth?ref=${refId}&redirect=${encodeURIComponent('/quy-trinh')}&scroll=${scroll}`);
                                            }
                                        }}
                                    >
                                        {translations.registerAction[language]}
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
                <AlertDialogContent className="max-w-[90vw] md:max-w-md rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t.referralConfirmation.title[language]}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t.referralConfirmation.description[language].replace('[COMPANY]', agencyName)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row gap-2 sm:gap-0">
                        <AlertDialogCancel className="flex-1 mt-0">{t.referralConfirmation.cancel[language]}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirm} className="flex-1 bg-[#19A6DF] hover:bg-[#19A6DF]/90">
                            {t.referralConfirmation.confirm[language]}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export function AgencyReferralCard() {
    return (
        <Suspense fallback={null}>
            <ReferralContent />
        </Suspense>
    );
}
